import * as Rx from 'rxjs';
import { bufferTime, filter, map } from 'rxjs/operators';
import { RequestDataPayload } from '../../actions';

/**
 * Batches requests that occur within `windowTime` milliseconds of each other,
 * such that at the end of the interval, requests to the same part of the
 * datastore are grouped together into one request. Requests will not be batched
 * if they differ in either collection or authUid.
 *
 * Example: With a window size of 3 seconds, a request for user with id 1 (U1),
 * then for ids 2 and 3 (U2:3) one second later, then for project 5 (P5)
 *
 * 0     1     2     3     4     5s
 * U1----U2:3----P5--------------|
 *        batchRequests(3000)
 * ------------------B-----------|
 *
 * Where B is the list [ U1:2:3, P5 ]
 */
export function batchRequests(
  windowTime: number,
  scheduler: Rx.SchedulerLike = Rx.asyncScheduler,
): (
  source$: Rx.Observable<RequestDataPayload<any>>,
) => Rx.Observable<ReadonlyArray<RequestDataPayload<any>>> {
  if (windowTime === 0) {
    return (source$: Rx.Observable<RequestDataPayload<any>>) =>
      source$.pipe(map(request => [request]));
  }

  return (requestStream$: Rx.Observable<RequestDataPayload<any>>) =>
    requestStream$.pipe(
      bufferTime(windowTime, scheduler),
      filter(requests => requests.length > 0),
      map(requests => {
        const requestsToBatch = requests.reduce(
          (toBatch, req) => {
            const key = `${req.type}-${req.ref.path.authUid}`;
            const ids = req.ref.path.ids || [];
            let existingIds: ReadonlyArray<string> = [];

            // TODO: Add batch limits to prevent overloading backend
            if (toBatch[key]) {
              existingIds = toBatch[key].ref.path.ids || [];
              toBatch[key] = {
                ...toBatch[key],
                ref: {
                  ...toBatch[key].ref,
                  path: {
                    ...toBatch[key].ref.path,
                    ids: [...existingIds, ...ids],
                  },
                },
                // A batched request id is the concatenation of the orginal request ids
                clientRequestIds: [
                  ...toBatch[key].clientRequestIds,
                  ...req.clientRequestIds,
                ],
              };
              return toBatch;
            }

            return {
              ...toBatch,
              [key]: {
                type: req.ref.path.collection,
                ref: {
                  path: {
                    collection: req.ref.path.collection,
                    authUid: req.ref.path.authUid,
                    ids,
                  },
                },
                clientRequestIds: req.clientRequestIds,
              },
            };
          },
          {} as { [key: string]: RequestDataPayload<any> },
        );

        return Object.values(requestsToBatch);
      }),
    );
}
